import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Ruler, Home, Send, CheckCircle2, Building2, FileText } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import AttachmentsUnlock from "../components/AttachmentsUnlock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

export default function DealRoomPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [expired, setExpired] = useState(false);
  const [interestOpen, setInterestOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [msg, setMsg] = useState({ author_name: "", body: "" });
  const [activeIdx, setActiveIdx] = useState(0);

  const load = async () => {
    try {
      const { data } = await publicApi.get(`/public/deal-room/${token}`);
      setData(data);
    } catch (e) {
      if (e.response?.status === 410) setExpired(true);
      else setNotFound(true);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const submitInterest = async () => {
    try {
      await publicApi.post(`/public/deal-room/${token}/interest`, form);
      setSent(true); toast.success("Grazie, ti richiameremo al più presto!");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const sendMessage = async () => {
    if (!msg.body.trim()) return;
    try {
      await publicApi.post(`/public/deal-room/${token}/messages`, msg);
      setMsg({ ...msg, body: "" });
      load();
    } catch (e) { toast.error("Impossibile inviare"); }
  };

  if (notFound) return <FullPage title="Link non valido" text="Questa opportunità non è più disponibile." />;
  if (expired) return <FullPage title="Link scaduto" text="Questa Deal Room non è più accessibile." />;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-muted/30"><Skeleton className="h-64 w-full max-w-3xl" /></div>;

  const p = data.property;
  const photos = data.photos || [];
  const cover = photos[activeIdx] ? `${BACKEND_URL}/api/files/${photos[activeIdx].storage_path}` :
                (p.hero_image ? (p.hero_image.startsWith("http") ? p.hero_image : `${BACKEND_URL}${p.hero_image}`) : null);

  return (
    <div className="min-h-screen bg-muted/20" data-testid="public-deal-room">
      {/* Header */}
      <header className="bg-[hsl(222,47%,11%)] text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
            <Building2 className="w-4 h-4" />
          </div>
          <div className="font-heading font-semibold tracking-tight">LOOK OFF MARKET</div>
          <div className="ml-auto text-xs text-slate-300">Deal Room privata</div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
        {/* Signature banner — the product's method */}
        {data.signature && (
          <section className="relative overflow-hidden rounded-xl bg-[hsl(222,47%,11%)] text-white p-6 md:p-8" data-testid="public-signature">
            <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, rgba(59,130,246,0.35), transparent 45%), radial-gradient(circle at 85% 75%, rgba(59,130,246,0.20), transparent 45%)" }} />
            <div className="relative">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-1 h-6 bg-primary rounded-full" />
                <span className="text-[10px] uppercase tracking-[0.3em] text-slate-300 font-semibold">LOOK OFF MARKET™</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 mb-3">Opportunity Index</div>
                  <div className="flex items-baseline gap-1">
                    <span className={`font-heading text-6xl md:text-7xl font-bold tabular-nums leading-none ${data.signature.total >= 80 ? "text-emerald-400" : data.signature.total >= 60 ? "text-blue-400" : "text-amber-400"}`}>{data.signature.total}</span>
                    <span className="text-xl text-slate-400">/100</span>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3 text-xs">
                    <div className="inline-flex items-center gap-1.5 bg-white/5 rounded-md px-2.5 py-1">
                      <span className="text-slate-400">Probabilità chiusura</span>
                      <span className="font-heading font-semibold tabular-nums text-emerald-400">{data.signature.closing_probability}%</span>
                    </div>
                    <div className="inline-flex items-center gap-1.5 bg-white/5 rounded-md px-2.5 py-1">
                      <span className="text-slate-400">Tempo stimato</span>
                      <span className="font-heading font-semibold tabular-nums text-white">{data.signature.estimated_days} giorni</span>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <div className="text-xs uppercase tracking-wider text-slate-400 mb-4">Perché?</div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                    {data.signature.signature_reasons.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className={`inline-block w-4 h-4 rounded-full mt-0.5 shrink-0 ${r.ok ? "bg-emerald-400" : "bg-slate-600"}`} />
                        <span className={r.ok ? "text-white" : "text-slate-500 line-through"}>{r.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-white/10 text-[11px] text-slate-500">
                Valutazione strutturata basata su margine, posizione, complessità legale, documentazione, tempo di operazione e compatibilità investitori.
              </div>
            </div>
          </section>
        )}

        {/* Hero */}
        <Card className="border-border/60 overflow-hidden">
          <div className="aspect-[16/9] md:aspect-[21/9] bg-muted relative">
            {cover ? <img src={cover} alt={p.title} className="w-full h-full object-cover" /> : <div className="flex h-full items-center justify-center text-muted-foreground"><Home className="w-16 h-16" strokeWidth={0.5} /></div>}
          </div>
          {photos.length > 1 && (
            <div className="flex gap-2 p-3 overflow-x-auto scrollbar-thin border-t border-border/60">
              {photos.map((ph, i) => (
                <button key={ph.id} onClick={() => setActiveIdx(i)}
                  className={`w-20 h-14 rounded-md overflow-hidden shrink-0 border-2 ${activeIdx === i ? "border-primary" : "border-transparent opacity-70 hover:opacity-100"}`}>
                  <img src={`${BACKEND_URL}/api/files/${ph.storage_path}`} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="p-6 md:p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">{p.title}</h1>
                <div className="text-muted-foreground flex items-center gap-1 mt-2"><MapPin className="w-4 h-4" />{p.address || ""}{p.city ? `, ${p.city}` : ""}{p.province ? ` (${p.province})` : ""}</div>
              </div>
              <Badge className="bg-primary/10 text-primary border-transparent capitalize text-sm">{p.status?.replace("_", " ")}</Badge>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <Meta label="Superficie" value={`${p.size_sqm || 0} mq`} icon={Ruler} />
              <Meta label="Locali" value={`${p.rooms || 0} · ${p.bathrooms || 0} bagni`} />
              <Meta label="Tipologia" value={String(p.property_type || "").replace(/^\w/, (c) => c.toUpperCase())} />
              {p.year_built ? <Meta label="Anno" value={p.year_built} /> : <Meta label="Città" value={p.city || "—"} />}
            </div>

            {(p.price != null) && (
              <div className="mt-6 pt-6 border-t border-border/60 grid grid-cols-1 md:grid-cols-3 gap-4">
                <BigStat label="Prezzo indicativo" value={`€ ${fmt(p.price)}`} />
                {p.estimated_value ? <BigStat label="Valore stimato" value={`€ ${fmt(p.estimated_value)}`} /> : null}
                {p.potential_margin ? <BigStat label="Margine potenziale" value={`€ ${fmt(p.potential_margin)}`} accent="text-emerald-600" /> : null}
              </div>
            )}

            {p.description && <p className="mt-6 pt-6 border-t border-border/60 text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">{p.description}</p>}
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Interest form + chat */}
          <div className="lg:col-span-2 space-y-6">
            {/* Interest */}
            <Card className="border-border/60 p-6" data-testid="interest-card">
              {sent ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                  <h3 className="font-heading font-semibold text-lg">Grazie per l'interesse!</h3>
                  <p className="text-sm text-muted-foreground mt-1">Il nostro team ti contatterà molto presto.</p>
                </div>
              ) : (
                <>
                  <h3 className="font-heading text-lg font-semibold">Sei interessato a questa opportunità?</h3>
                  <p className="text-sm text-muted-foreground mt-1">Lasciaci i tuoi dati, ti ricontatteremo entro 24 ore.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                    <div><Label className="text-xs">Nome e cognome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="int-name" /></div>
                    <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="int-email" /></div>
                    <div><Label className="text-xs">Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="int-phone" /></div>
                    <div><Label className="text-xs">Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Es. budget, timing…" data-testid="int-note" /></div>
                  </div>
                  <Button onClick={submitInterest} className="mt-4 w-full md:w-auto" disabled={!form.email && !form.phone} data-testid="int-submit">
                    <Send className="w-4 h-4 mr-2" /> Manifesta interesse
                  </Button>
                </>
              )}
            </Card>

            {/* Chat */}
            <Card className="border-border/60 p-6" data-testid="chat-card">
              <h3 className="font-heading text-lg font-semibold mb-2">Fai una domanda</h3>
              <div className="space-y-2 mb-4 max-h-72 overflow-y-auto scrollbar-thin">
                {data.messages.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">Nessun messaggio ancora. Scrivi per primo!</p>}
                {data.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "host" ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.direction === "host" ? "bg-muted" : "bg-primary text-primary-foreground"}`}>
                      <div className="text-[10px] font-medium opacity-80">{m.direction === "host" ? "LOOK OFF MARKET" : m.author_name}</div>
                      <div className="mt-0.5 whitespace-pre-wrap">{m.body}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input placeholder="Il tuo nome" value={msg.author_name} onChange={(e) => setMsg({ ...msg, author_name: e.target.value })} data-testid="msg-name" />
                <Textarea rows={1} placeholder="Scrivi un messaggio…" value={msg.body} onChange={(e) => setMsg({ ...msg, body: e.target.value })} className="md:col-span-2" data-testid="msg-body" />
              </div>
              <Button onClick={sendMessage} className="mt-3 gap-2" disabled={!msg.body.trim()} data-testid="msg-send"><Send className="w-4 h-4" /> Invia</Button>
            </Card>
          </div>

          {/* Documents — gated behind Stripe unlock (bundle) */}
          <div className="space-y-4">
            {p.id && (
              <AttachmentsUnlock propertyId={p.id} anonymous={true} unlockKeyStorage={`unlock_email_${p.id}`} />
            )}
            {p.owner && (
              <Card className="border-border/60 p-6">
                <h3 className="font-heading font-medium mb-2">Referente</h3>
                <div className="text-sm">{p.owner.name}</div>
                {p.owner.email && <div className="text-xs text-muted-foreground">{p.owner.email}</div>}
                {p.owner.phone && <div className="text-xs text-muted-foreground">{p.owner.phone}</div>}
              </Card>
            )}
          </div>
        </div>

        <footer className="text-center text-xs text-muted-foreground pt-8 pb-4">
          Deal Room riservata · Powered by LOOK OFF MARKET
        </footer>
      </main>
    </div>
  );
}

function fmt(n) { return Intl.NumberFormat("it-IT").format(Math.round(n || 0)); }
function BigStat({ label, value, accent }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className={`font-heading text-2xl font-semibold tabular-nums mt-1 ${accent || ""}`}>{value}</div></div>;
}
function Meta({ label, value, icon: Icon }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />}{label}</div><div className="font-medium mt-1">{value}</div></div>;
}
function FullPage({ title, text }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/30">
      <Card className="max-w-md w-full p-8 text-center border-border/60">
        <h1 className="font-heading text-2xl font-semibold">{title}</h1>
        <p className="text-muted-foreground text-sm mt-2">{text}</p>
      </Card>
    </div>
  );
}
